// pages/HomePage.js
import React from 'react'
import styled from 'styled-components'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import BannerComponent from '../components/BannerComponent'
import Seo from '../components/Seo'
import { sizes } from '../utils/breakpoints'
import { getLangFromPath, withLang } from '../utils/i18nRoutes'

const Home = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const lang = getLangFromPath(location.pathname)

  return (
    <Container>
      <Seo
        title={t('seo.home.title')}
        description={t('seo.home.description')}
        path="/"
      />
      <HeroWrapper>
        <BannerComponent />

        <Teasers>
          <Teaser to={withLang('/servicios/autenticidad-contenido-digital', lang)}>
            <TeaserBadge>{t('home.teasers.c2pa.tag')}</TeaserBadge>
            <TeaserTitle>{t('home.teasers.c2pa.title')}</TeaserTitle>
            <TeaserText>{t('home.teasers.c2pa.text')}</TeaserText>
            <TeaserLink>{t('home.teasers.c2pa.link')}</TeaserLink>
          </Teaser>

          <Teaser to={withLang('/servicios/crypto-agilidad-cbom', lang)}>
            <TeaserBadge>{t('home.teasers.cryptoAgilidad.tag')}</TeaserBadge>
            <TeaserTitle>{t('home.teasers.cryptoAgilidad.title')}</TeaserTitle>
            <TeaserText>{t('home.teasers.cryptoAgilidad.text')}</TeaserText>
            <TeaserLink>{t('home.teasers.cryptoAgilidad.link')}</TeaserLink>
          </Teaser>

          <Teaser to={withLang('/talento', lang)}>
            <TeaserTitle>{t('home.teasers.talento.title')}</TeaserTitle>
            <TeaserText>{t('home.teasers.talento.text')}</TeaserText>
            <TeaserLink>{t('home.teasers.talento.link')}</TeaserLink>
          </Teaser>
        </Teasers>

        <TeaserDots aria-hidden="true">
          <TeaserDot />
          <TeaserDot />
          <TeaserDot />
        </TeaserDots>
      </HeroWrapper>
    </Container>
  )
}

export default Home

const Container = styled.div``

const HeroWrapper = styled.div`
  position: relative;
`

const Teasers = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 24px;
  max-width: 1080px;
  margin: -300px auto 75px;
  padding: 0 24px;

  @media (max-width: ${sizes.tablet}px) {
    margin: 32px auto 56px;
  }

  @media (max-width: ${sizes.mobile}px) {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 56px;
    flex-wrap: nowrap;
    justify-content: flex-start;
    gap: 16px;
    margin: 0;
    padding: 16px 20px 4px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`

const TeaserDots = styled.div`
  display: none;

  @media (max-width: ${sizes.mobile}px) {
    display: flex;
    justify-content: center;
    gap: 8px;
    position: absolute;
    left: 0;
    right: 0;
    bottom: 10px;
  }
`

const TeaserDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.text.onDark};
  opacity: 0.5;
`

const Teaser = styled(Link)`
  position: relative;
  flex: 1 1 280px;
  max-width: 330px;
  box-sizing: border-box;
  min-height: 225px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  background-color: ${({ theme }) => theme.colors.background.card};
  border-radius: 18px;
  box-shadow: ${({ theme }) => theme.shadows.card};
  padding: 36px;
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-6px);
    box-shadow: ${({ theme }) => theme.shadows.cardHover};
  }

  &:hover span {
    text-decoration: underline;
  }

  @media (max-width: ${sizes.mobile}px) {
    flex: 0 0 78%;
    max-width: 78%;
    min-height: 165px;
    padding: 18px;
    gap: 4px;
    scroll-snap-align: center;
  }
`

const TeaserBadge = styled.span`
  position: absolute;
  top: -11px;
  left: 28px;
  font-family: ${({ theme }) => theme.fonts.accentBold};
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.brand.dark};
  background-color: ${({ theme }) => theme.colors.brand.green};
  padding: 4px 10px;
  border-radius: 999px;
  box-shadow: ${({ theme }) => theme.shadows.card};

  @media (max-width: ${sizes.mobile}px) {
    top: -9px;
    left: 22px;
  }
`

const TeaserTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 22px;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;

  @media (max-width: ${sizes.mobile}px) {
    font-size: 16px;
  }
`

const TeaserText = styled.p`
  font-family: ${({ theme }) => theme.fonts.accentRegular};
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;

  @media (max-width: ${sizes.mobile}px) {
    font-size: 12px;
  }
`

const TeaserLink = styled.span`
  font-family: ${({ theme }) => theme.fonts.accentBold};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.accent};
`
